import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Heart } from "lucide-react";

export default function ApplyAsCounselor() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    title: "",
    license_number: "",
    years_experience: 0,
    session_price: 1000,
    specialties: "",
    languages: "",
    bio: "",
    why_mindora: "",
    linkedin_url: "",
    password: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if ((form.password || "").length < 8) {
      toast.error("Choose a password at least 8 characters long.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/therapist-applications", {
        ...form,
        years_experience: Number(form.years_experience) || 0,
      });
      setSubmitted(true);
      toast.success("Thank you. Your application is in.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="page-apply-success">
        <Heart size={28} className="mx-auto text-mindora-red" fill="#A73C35" />
        <h1 className="font-serif text-5xl text-mindora-ink leading-tight mt-6">
          We've received your application.
        </h1>
        <p className="mt-5 text-lg text-mindora-muted leading-relaxed">
          Thank you for wanting to be part of Mindora. A member of our clinical team will review your profile and
          reach out within seven days. If we need supporting documents (license, references) we'll ask by email.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/directory" className="rounded-full border border-mindora-line px-6 py-3 text-sm text-mindora-ink hover:bg-mindora-panel">Back to counselors</Link>
          <button onClick={() => navigate("/")} data-testid="apply-success-home" className="btn-primary rounded-full px-6 py-3 text-sm">Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20" data-testid="page-apply-counselor">
      <Link to="/directory" className="inline-flex items-center gap-2 text-sm text-mindora-muted hover:text-mindora-red mb-8" data-testid="apply-back-link">
        <ArrowLeft size={14} /> back to counselors
      </Link>

      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
        <span className="editorial-rule" />Apply to practice on Mindora
      </div>
      <h1 className="font-serif text-5xl lg:text-6xl text-mindora-ink leading-tight">
        For psychologists,<br /><em className="italic text-mindora-red">counselors, and therapists.</em>
      </h1>
      <p className="mt-5 text-mindora-muted leading-relaxed max-w-2xl">
        Mindora is built around honesty and care. If you are a licensed clinician who wants to meet young people
        where they actually are — in their stories — we'd love to hear from you. Tell us a little about yourself
        below; we review every application personally.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-7" data-testid="apply-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Full name">
            <Input required value={form.name} onChange={set("name")} data-testid="apply-name-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="Dr. Anjali Rao" />
          </Field>
          <Field label="Email">
            <Input type="email" required value={form.email} onChange={set("email")} data-testid="apply-email-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="you@clinic.com" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Professional title">
            <Input required value={form.title} onChange={set("title")} data-testid="apply-title-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="Clinical Psychologist, PhD" />
          </Field>
          <Field label="License / registration number">
            <Input value={form.license_number} onChange={set("license_number")} data-testid="apply-license-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="(Optional — we'll verify by email)" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Field label="Years of practice">
            <Input type="number" min="0" max="80" required value={form.years_experience} onChange={set("years_experience")} data-testid="apply-years-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" />
          </Field>
          <Field label="Session price (₹ per session)">
            <Input type="number" min="0" max="100000" required value={form.session_price} onChange={set("session_price")} data-testid="apply-price-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="1000" />
          </Field>
          <Field label="LinkedIn (optional)">
            <Input value={form.linkedin_url} onChange={set("linkedin_url")} data-testid="apply-linkedin-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="https://linkedin.com/in/…" />
          </Field>
        </div>

        <Field label="Specialties (comma-separated)">
          <Input required value={form.specialties} onChange={set("specialties")} data-testid="apply-specialties-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="Anxiety, Burnout, Identity" />
        </Field>

        <Field label="Languages you practice in (comma-separated)">
          <Input required value={form.languages} onChange={set("languages")} data-testid="apply-languages-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red" placeholder="English, Hindi" />
        </Field>

        <Field label="A short bio">
          <Textarea required rows={5} value={form.bio} onChange={set("bio")} data-testid="apply-bio-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif text-lg leading-relaxed" placeholder="How you work, what young people tend to bring to you, your style." />
        </Field>

        <Field label="Why Mindora?">
          <Textarea required rows={5} value={form.why_mindora} onChange={set("why_mindora")} data-testid="apply-why-input" className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif text-lg leading-relaxed" placeholder="What draws you to this kind of work?" />
        </Field>

        <div className="rounded-2xl border border-mindora-line bg-mindora-panel/60 p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-mindora-red mb-2">Choose your login password</div>
          <p className="text-sm text-mindora-muted mb-4 leading-relaxed">
            This is the password you'll use to sign in once Jiya approves your application. Pick something memorable — at least 8 characters.
          </p>
          <Field label="Password (min 8 characters)">
            <Input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={set("password")}
              data-testid="apply-password-input"
              className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </Field>
        </div>

        <Button type="submit" disabled={busy} data-testid="apply-submit-button" className="btn-primary rounded-full px-8 py-3 h-auto text-sm tracking-wide">
          {busy ? "Sending…" : "Submit application"}
        </Button>
        <p className="text-xs text-mindora-muted">
          By submitting, you agree to be contacted about your application. We never share your details.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
